import { Navigate, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api.js";
import { pluralize } from "../ui.js";
import { ChevronRightIcon, StoreIcon } from "../icons.js";
import { ErrorPage, errorDetails } from "../error-page.js";

/**
 * Landing step: choose which restaurant to manage.
 *
 * Always shown, even for a single restaurant: signing in lands here every time, so the first screen is the
 * same for every owner and is where they confirm which location they are about to manage. With several, it is
 * an explicit choice rather than a dropdown the user might miss.
 */
export function RestaurantPicker() {
  const navigate = useNavigate();
  const mine = useQuery({ queryKey: ["restaurants"], queryFn: () => api.listRestaurants() });
  const owned = mine.data?.restaurants;

  if (mine.isLoading) return <CenteredCard>Loading your restaurants…</CenteredCard>;

  if (mine.error) {
    return <ErrorPage details={errorDetails(mine.error, { context: "load restaurants" })} />;
  }

  if (!owned || owned.length === 0) {
    /**
     * No restaurant means one of two things, and onboarding tells them apart: either they have just
     * signed up and need to fill in the form, or they have already applied and are waiting on us.
     * Redirecting rather than showing a dead end is the point — the previous copy told a
     * freshly-signed-up owner to contact support, which is the opposite of self-serve.
     */
    return <Navigate to="/onboarding" replace />;
  }

  return (
    <CenteredCard>
      <h1>Choose a location</h1>
      <p className="muted">You manage {pluralize(owned.length, "location")}.</p>
      <ul className="picker-list">
        {owned.map((r) => (
          <li key={r.restaurantId}>
            <button
              className="picker-item"
              onClick={() => navigate(`/r/${r.restaurantId}/orders`)}
            >
              <StoreIcon />
              <span className="picker-name">
                {r.displayName ?? r.restaurantId}
                {/* Show the id too when it differs, so an operator browsing many locations
                    can tell near-identical names apart. */}
                {r.displayName ? <em className="muted"> {r.restaurantId}</em> : null}
              </span>
              <ChevronRightIcon className="chev" />
            </button>
          </li>
        ))}
      </ul>
    </CenteredCard>
  );
}

export function CenteredCard({ children }: { children: React.ReactNode }) {
  return (
    <main className="centered">
      <div className="card">{children}</div>
    </main>
  );
}
